"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Menu, X, Loader, Download } from "lucide-react";
function LeadMagnetForm({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [pdfReady, setPdfReady] = useState(false);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      setPdfReady(true);
      onSuccess();
    }
  }, [isSubmitted, submitError, onSuccess]);
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/pdf/due_diligence_checklist.pdf';
    link.download = 'Due_Diligence_Checklist.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  if (pdfReady) {
    return <div className="p-6 bg-accent/10 border border-accent/30 rounded-lg text-center">
        <h3 className="font-display text-xl font-semibold text-foreground mb-3">
          Thank You!
        </h3>
        <p className="text-muted-foreground mb-6">
          Your Due Diligence Checklist is ready for download.
        </p>
        <Button onClick={handleDownload} className="bg-accent text-foreground-accent hover:bg-accent-hover transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
          <Download className="mr-2 size-5" />
          Download Checklist
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
            <FormDescription className="text-muted-foreground text-sm">
              Enter your email to access the Due Diligence Checklist
            </FormDescription>
            <FormControl>
              <Input type="email" placeholder="you@example.com" className="border-border focus:border-accent focus:ring-accent/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-4">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-6 bg-primary text-foreground-primary hover:bg-primary-hover transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : "Get Your Checklist"}
      </Button>
    </Form>;
}
function LeadMagnetModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-primary/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-strong max-w-md w-full mx-4 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-display text-2xl font-bold text-foreground">
            Access the Data Room
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-secondary-light" aria-label="Close modal">
            <X className="size-5" />
          </Button>
        </div>

        <div className="p-6">
          <p className="text-muted-foreground mb-6">
            Get instant access to our comprehensive Due Diligence Checklist. See exactly what transparency looks like.
          </p>

          <FormProvider formId="data-room-lead-magnet" sectionName="header-data-room-cta" defaultValues={{
          email: ""
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Get Your Checklist" successMessage="Thank you! Your checklist is ready for download.">
            <LeadMagnetForm onSuccess={() => {}} />
          </FormProvider>
        </div>
      </div>
    </div>;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden lg:flex gap-1">
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-foreground transition-colors duration-[var(--duration-fast)] hover:!bg-transparent focus:!bg-transparent group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b shadow-medium lg:hidden">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-sm font-medium text-foreground hover:bg-secondary-light rounded-md transition-colors duration-[var(--duration-fast)]" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
            <Link to="/DataRoomPage" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full mt-2 bg-background text-accent border-2 border-accent hover:bg-accent hover:text-foreground-accent hover:!bg-transparent focus:!bg-transparent transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                Access the Data Room
              </Button>
            </Link>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={`sticky top-0 z-50 w-full bg-background border-b transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "h-16 shadow-soft" : "h-24"}`}>
        <div className="container mx-auto px-4 h-full flex items-center justify-between gap-8">
          <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-opacity">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1531884/img/glass_house_properties_logo.png" alt="Glass House Properties" className="h-7" />
          </Link>

          <div className="flex-1 flex justify-center">
            <MenuProvider menu_id="33">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          <Link to="/DataRoomPage">
            <Button className="hidden lg:inline-flex bg-background text-accent border-2 border-accent hover:bg-accent hover:text-foreground-accent hover:!bg-transparent focus:!bg-transparent hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
              Access the Data Room
            </Button>
          </Link>
        </div>
      </header>

      <LeadMagnetModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>;
}